/**
 * VI → EN dictionary for the DEMO-ONLY language toggle used in Meta App Review
 * screencasts. Keys are the EXACT trimmed Vietnamese text that appears as a whole
 * DOM text node (or placeholder/title/aria-label attribute); values are the English
 * shown when the reviewer switches the toggle to English. Matching is whole-node and
 * exact, so short keys (e.g. "Tên") only replace a node whose entire text is that key.
 *
 * ADD-ONLY: consumed solely by i18n/DemoLang.tsx. No existing code imports this.
 */
export const VI_EN: Record<string, string> = {
  // ---- Login ----
  "Email không hợp lệ": "Invalid email",
  "Đăng nhập thành công": "Signed in successfully",
  "Đăng nhập thất bại": "Sign-in failed",
  "Đăng nhập để quản lý quảng cáo của bạn": "Sign in to manage your ads",
  "Đăng nhập": "Sign in",
  "Mật khẩu": "Password",
  "Chưa hỗ trợ": "Not supported yet",
  "Quên mật khẩu?": "Forgot password?",
  "Chưa có tài khoản?": "Don't have an account?",
  "Đăng ký ngay": "Register now",

  // ---- Shell / nav / shared ----
  "Mở menu": "Open menu",
  "Điều hướng chính": "Main navigation",
  "Đăng xuất": "Log out",
  "Hành động": "Actions",
  "Trạng thái": "Status",
  "Hủy": "Cancel",
  "Lưu": "Save",
  "Xóa": "Delete",
  "Tên": "Name",
  "Có": "There are",
  "Không": "None",
  "Tất cả": "All",
  "Tất cả tài khoản": "All accounts",
  "Tất cả trạng thái": "All statuses",
  "Từ ngày": "From date",
  "Đến ngày": "To date",
  "Khoảng thời gian": "Date range",
  "Hôm nay": "Today",
  "Hôm qua": "Yesterday",
  "7 ngày qua": "Last 7 days",
  "14 ngày qua": "Last 14 days",
  "28 ngày qua": "Last 28 days",
  "30 ngày qua": "Last 30 days",
  "Tuần này": "This week",
  "Tháng này": "This month",
  "Tùy chỉnh": "Custom",
  "Tìm kiếm": "Search",
  "Xem tất cả": "View all",
  "Mở trong Trình quản lý quảng cáo": "Open in Ads Manager",
  "🔄 Làm mới": "🔄 Refresh",
  "Đã làm mới tất cả": "Refreshed everything",
  "Làm mới thất bại": "Refresh failed",
  "Đã đồng bộ": "Synced",
  "tài khoản quảng cáo": "ad accounts",
  "Tài khoản quảng cáo": "Ad accounts",
  "Tài khoản BM": "Business Manager Accounts",
  "Quy tắc": "Rules",

  // ---- Status / enum badges (labels.ts) ----
  "Đang hoạt động": "Active",
  "Tạm dừng": "Pause",
  "Đã lưu trữ": "Archived",
  "Đã xóa": "Deleted",
  "Vô hiệu hóa": "Disabled",
  "Thành công": "Success",
  "Thất bại": "Failed",
  "Bỏ qua": "Skipped",
  "Bị chặn bởi guardrail": "Blocked by guardrail",
  "Ngưỡng doanh thu": "Revenue threshold",
  "Ngưỡng CPA": "CPA threshold",
  "Ngưỡng ROAS": "ROAS threshold",
  "Ngưỡng chi tiêu": "Spend threshold",
  "Tăng ngân sách": "Increase budget",
  "Giảm ngân sách": "Decrease budget",
  "Kích hoạt": "Activate",
  "Nhóm quảng cáo": "Ad set",

  // ---- Facebook Accounts (BM) — business_management ----
  "Không rõ": "Unknown",
  "Đã hết hạn": "Expired",
  "Sắp hết hạn": "Expiring soon",
  "Còn hiệu lực": "Valid",
  "Không thể tải danh sách tài khoản Facebook": "Unable to load the list of Facebook accounts",
  "Không thể bắt đầu đăng nhập Facebook": "Unable to start Facebook login",
  "Đã kết nối tài khoản Facebook thành công": "Facebook account connected successfully",
  "Kết nối Facebook thất bại": "Facebook connection failed",
  "Đã liên kết tài khoản Facebook": "Facebook account linked",
  "Liên kết tài khoản thất bại": "Failed to link the account",
  "Đồng bộ tài khoản quảng cáo thất bại": "Failed to sync ad accounts",
  "Đã xóa liên kết tài khoản Facebook": "Facebook account link removed",
  "Xóa tài khoản thất bại": "Failed to delete the account",
  "Tên doanh nghiệp": "Business name",
  "Trạng thái Token": "Token status",
  "Hết hạn": "Expires",
  "Ngày tạo": "Created date",
  "Đồng bộ Ad Accounts": "Sync Ad Accounts",
  "Quản lý các tài khoản Business Manager đã liên kết.": "Manage your linked Business Manager accounts.",
  "Kết nối bằng Facebook": "Connect with Facebook",
  "Liên kết thủ công": "Link manually",
  "Chưa có tài khoản Facebook nào. Hãy liên kết tài khoản đầu tiên.": "No Facebook accounts yet. Link your first account.",
  "Liên kết tài khoản Facebook": "Link a Facebook account",
  "Liên kết": "Link",
  "Ví dụ: 1234567890": "Example: 1234567890",
  "Ví dụ: 9876543210": "Example: 9876543210",
  "Dán System User Access Token": "Paste the System User Access Token",
  "Access Token được mã hóa và lưu trữ an toàn, không bao giờ hiển thị lại.": "The Access Token is encrypted and stored securely, and is never shown again.",
  "Xác nhận xóa": "Confirm deletion",
  "Bạn có chắc muốn xóa liên kết tài khoản": "Are you sure you want to remove the link for account",
  "? Toàn bộ tài khoản quảng cáo liên quan cũng sẽ bị xóa.": "? All related ad accounts will also be deleted.",

  // ---- Ad Accounts — ads_read ----
  "Không thể tải tài khoản Facebook": "Unable to load Facebook accounts",
  "Không thể tải tài khoản quảng cáo": "Unable to load ad accounts",
  "Vui lòng chọn một tài khoản Facebook để đồng bộ": "Please select a Facebook account to sync",
  "Đồng bộ thất bại": "Sync failed",
  "Giới hạn không hợp lệ (để trống = bỏ giới hạn)": "Invalid limit (leave empty to remove the limit)",
  "Đã bỏ giới hạn chi tiêu": "Spend limit removed",
  "Đã đặt giới hạn": "Limit set to",
  "Đặt giới hạn thất bại": "Failed to set limit",
  "lỗi:": "errors:",
  "Đặt giới hạn hàng loạt thất bại": "Bulk limit update failed",
  "Không thể tải danh sách mẫu": "Unable to load the template list",
  "Vui lòng chọn mẫu": "Please select a template",
  "Vui lòng chọn ít nhất 1 tài khoản đích": "Please select at least 1 target account",
  "Ngân sách/ngày không hợp lệ": "Invalid daily budget",
  "Lỗi": "Error",
  "tài khoản:": "accounts:",
  "Tạo chiến dịch từ mẫu thất bại": "Failed to create campaign from template",
  "(không tên)": "(no name)",
  "ID tài khoản": "Account ID",
  "Chủ sở hữu": "Owner",
  "Tiền tệ": "Currency",
  "Đuôi thẻ": "Card last digits",
  "Chưa có thẻ": "No card yet",
  "Tổng tiêu": "Total spent",
  "Giới hạn": "Limit",
  "Mở trang thanh toán Facebook để thêm/verify thẻ": "Open the Facebook payment page to add/verify a card",
  "Quản lý thẻ": "Manage card",
  "Thêm thẻ": "Add card",
  "Đặt giới hạn": "Set limit",
  "Danh sách tài khoản quảng cáo đã đồng bộ từ Facebook.": "List of ad accounts synced from Facebook.",
  "Tạo chiến dịch": "Create campaign",
  "Tài khoản doanh nghiệp (BM)": "Business Manager account (BM)",
  "Tất cả BM": "All BMs",
  "Tìm theo tên hoặc ID...": "Search by name or ID...",
  "tài khoản đã chọn": "accounts selected",
  "Giới hạn (VND) — trống = bỏ": "Limit (VND) — empty = none",
  "Đặt giới hạn hàng loạt": "Set limit in bulk",
  "Tạo chiến dịch cho TK đã chọn": "Create campaign for selected accounts",
  "Áp cho tất cả tài khoản đã chọn.": "Applies to all selected accounts.",
  "Bỏ chọn": "Clear selection",
  "Chưa có tài khoản quảng cáo. Hãy chọn tài khoản BM và nhấn Đồng bộ.": "No ad accounts yet. Select a BM account and click Sync.",
  "Đặt giới hạn chi tiêu tài khoản": "Set account spend limit",
  "Tài khoản:": "Account:",
  "Giới hạn chi tiêu (VND) — để trống để bỏ giới hạn": "Spend limit (VND) — leave empty to remove the limit",
  "Ví dụ: 2500000": "Example: 2500000",
  "Quảng cáo sẽ tạm dừng khi tổng chi tiêu chạm giới hạn. Giới hạn tính trên": "Ads will pause when total spend reaches the limit. The limit is based on",
  "tổng tiêu trọn đời": "lifetime total spend",
  "— nên đặt cao hơn \"Tổng tiêu\" hiện tại": "— set it higher than the current \"Total spent\"",
  ", nếu không tài khoản sẽ dừng ngay.": ", otherwise the account will stop immediately.",
  "Tạo chiến dịch từ mẫu": "Create campaign from template",
  "Tạo & Đăng": "Create & Publish",
  "Tạo (nháp)": "Create (draft)",
  "Mẫu": "Template",
  "Đang tải mẫu…": "Loading templates…",
  "Chọn mẫu": "Select a template",
  "Chưa có mẫu nào. Vào trang Chiến dịch, mở một chiến dịch chuẩn và bấm \"☆ Đánh dấu mẫu\" để thêm.": "No templates yet. Go to the Campaigns page, open a standard campaign, and click \"☆ Mark as template\" to add one.",
  "Tài khoản đích (chọn 1 hoặc nhiều)": "Target accounts (select 1 or more)",
  "Chọn tài khoản": "Select accounts",
  "Ngân sách/ngày — mỗi camp": "Daily budget — per campaign",
  "(theo tiền tệ TK)": "(in each account's currency)",
  "Ví dụ: 50000": "Example: 50000",
  "Số bản / tài khoản": "Copies / account",
  "Sẽ tạo": "Will create",
  "chiến dịch (": "campaigns (",
  "tài khoản ×": "accounts ×",
  "bản) · mỗi camp": "copies) · per campaign",
  "/ngày": "/day",
  "— chạy thật ngay khi tạo.": "— runs live immediately after creation.",
  "— lưu nháp (PAUSED).": "— saved as draft (PAUSED).",
  "⚠️ Bạn đang chọn nhiều tiền tệ (": "⚠️ You are selecting multiple currencies (",
  "). Số ngân sách nhập được áp theo tiền tệ CỦA TỪNG tài khoản — nên deploy riêng từng loại (vd 50000 = 50.000đ với TK VND nhưng = 50.000 USD với TK USD).": "). The budget you enter is applied in EACH account's own currency — deploy each currency separately (e.g. 50000 = 50,000 VND for a VND account but 50,000 USD for a USD account).",
  "Tuổi từ (để trống = theo mẫu)": "Age from (empty = use template)",
  "VD: 18": "e.g. 18",
  "Tuổi đến": "Age to",
  "VD: 45": "e.g. 45",
  "Tên chiến dịch mới": "New campaign name",
  "Để trống để dùng tên mẫu": "Leave empty to use the template name",
  "Đăng ngay (ACTIVE) — bỏ chọn để tạo bản nháp (PAUSED)": "Publish now (ACTIVE) — uncheck to create a draft (PAUSED)",
  "Tạo mới từ đầu (chiến dịch + nhóm QC + quảng cáo) giống mẫu 100%, chỉ đổi ngân sách. Cần tài khoản đích có quyền tạo + pixel/Trang được chia sẻ. Đây là thao tác live, tốn tiền thật khi đăng.": "Creates everything from scratch (campaign + ad set + ad) identical to the template 100%, changing only the budget. The target account must have creation permission + a shared pixel/Page. This is a live action that spends real money when published.",

  // ---- Campaigns — ads_management ----
  "Chiến dịch": "Campaigns",
  "Quản lý và tối ưu các chiến dịch quảng cáo.": "Manage and optimize your ad campaigns.",
  "Chiến dịch (đang chạy)": "Campaigns (running)",
  "Tổng đã tiêu (theo mốc)": "Total spent (in range)",
  "Tổng doanh thu (theo mốc)": "Total revenue (in range)",
  "ROAS tổng (thật)": "Total ROAS (real)",
  "Khoảng thời gian (chi tiêu)": "Date range (spend)",
  "Đang chạy (có chi tiêu)": "Running (with spend)",
  "đã chọn": "selected",
  "Cập nhật NS": "Update budget",
  "Đổi tên": "Rename",
  "Tên chiến dịch": "Campaign name",
  "Tài khoản QC / BM": "Ad Account / BM",
  "Ngân sách/ngày": "Daily budget",
  "Đã tiêu": "Spent",
  "Chuyển đổi": "Conversions",
  "Doanh thu": "Revenue",
  "ROAS thật": "Real ROAS",
  "Ngân sách": "Budget",
  "★ Bỏ mẫu": "★ Unmark template",
  "☆ Đánh dấu mẫu": "☆ Mark as template",
  "Sao chép": "Copy",
  "Chưa có chiến dịch. Chọn tài khoản quảng cáo và nhấn Đồng bộ.": "No campaigns yet. Select an ad account and click Sync.",
  "Sao chép chiến dịch": "Copy campaign",
  "Để trống để dùng tên mặc định": "Leave blank to use the default name",
  "Chỉnh sửa ngân sách": "Edit budget",
  "Ngân sách hằng ngày (VND)": "Daily budget (VND)",
  "Ví dụ: 500000": "e.g. 500,000",
  "Ngân sách trọn đời (VND)": "Lifetime budget (VND)",
  "Ví dụ: 5000000": "e.g. 5,000,000",
  "Chỉ cần nhập loại ngân sách bạn muốn thay đổi.": "Only enter the budget type you want to change.",
  "chiến dịch": "campaign(s)",
  "Áp dụng": "Apply",
  "Tên mới": "New name",
  "vd: Combo Week360 + Finance360": "e.g. Combo Week360 + Finance360",
  "Hậu tố (tuỳ chọn)": "Suffix (optional)",
  "vd: camp  (hoặc: set ngày 25/7)": "e.g. camp (or: ad set for Jul 25)",
  "Đánh số thứ tự tăng dần": "Number sequentially (ascending)",
  "bắt đầu từ": "start from",
  "Xem trước": "Preview",
  "(nhập tên mới)": "(enter a new name)",
  "Đặt tên mới cho tất cả camp đã chọn (không quan tâm tên cũ). Bật STT để tự đánh số 1, 2, 3… Camp đặc biệt (boosted post…) có thể bị Facebook chặn đổi tên.": "Set a new name for all selected campaigns (regardless of the old name). Turn on numbering to auto-number 1, 2, 3… Special campaigns (boosted posts…) may be blocked from renaming by Facebook.",
  "Vui lòng chọn đúng 1 tài khoản quảng cáo để đồng bộ chiến dịch": "Please select exactly 1 ad account to sync campaigns",
  "Đồng bộ chiến dịch thất bại": "Campaign sync failed",
  "Route chưa khớp campaign:": "Routes not matched to a campaign:",
  "Đồng bộ doanh thu thất bại": "Revenue sync failed",
  "TẠM DỪNG": "PAUSE",
  "KÍCH HOẠT": "ACTIVATE",
  "Xác nhận": "Confirm",
  "Thay đổi sẽ được gửi tới Facebook Ads Manager.": "The change will be sent to Facebook Ads Manager.",
  "Đã kích hoạt chiến dịch": "Campaign activated",
  "Đã tạm dừng chiến dịch": "Campaign paused",
  "Cập nhật trạng thái thất bại": "Failed to update status",
  "(Bản sao)": "(Copy)",
  "Đã sao chép chiến dịch": "Campaign copied",
  "Sao chép chiến dịch thất bại": "Failed to copy campaign",
  "Vui lòng nhập ít nhất một loại ngân sách": "Please enter at least one budget type",
  "Ngân sách không hợp lệ": "Invalid budget",
  "Đã cập nhật ngân sách": "Budget updated",
  "Cập nhật ngân sách thất bại": "Failed to update budget",
  "Đã bỏ đánh dấu mẫu": "Template mark removed",
  "Đã đánh dấu làm mẫu": "Marked as template",
  "Cập nhật mẫu thất bại": "Failed to update template",
  "camp không sửa được — Facebook chặn camp đặc biệt (boosted post / Advantage+ / ngân sách ở cấp nhóm QC).": "campaign(s) could not be edited — Facebook blocks special campaigns (boosted post / Advantage+ / budget at the ad-set level).",
  "Thao tác hàng loạt thất bại": "Bulk action failed",
  "Cập nhật ngân sách hàng loạt thất bại": "Bulk budget update failed",
  "Vui lòng nhập tên mới": "Please enter a new name",
  "Không tạo được tên hợp lệ": "Could not generate a valid name",
  "camp không đổi được tên — Facebook chặn camp đặc biệt.": "campaign(s) could not be renamed — Facebook blocks special campaigns.",
  "Đổi tên hàng loạt thất bại": "Bulk rename failed",

  // ---- Logs (audit log) — ads_management evidence ----
  "Nhật ký hoạt động": "Activity Log",
  "Theo dõi mọi hành động và quyết định của AI Agent.": "Track every action and decision made by the AI Agent.",
  "Lọc": "Filter",
  "Đặt lại": "Reset",
  "Thời gian": "Time",
  "Sự kiện": "Event",
  "Mô tả": "Description",
  "Lý do quyết định": "Decision Rationale",
  "Giá trị cũ → mới": "Old Value → New Value",
  "Chưa có nhật ký hoạt động nào.": "No activity logs yet.",
  "Không thể tải nhật ký": "Unable to load the log.",
  "Không thể tải nhật ký hoạt động": "Unable to load the activity log.",

  // ---- Dashboard ----
  "Bảng điều khiển": "Dashboard",
  "Kết quả kinh doanh & hoạt động của AI Agent.": "Business results & AI Agent activity.",
  "Không thể tải dữ liệu bảng điều khiển. Vui lòng thử lại.": "Could not load dashboard data. Please try again.",
  "Đã xảy ra lỗi không xác định": "An unknown error occurred",
  "Không thể tải dữ liệu bảng điều khiển": "Could not load dashboard data",
  "Token sắp hết hạn": "Token expiring soon",
  "tài khoản Facebook có Access Token sắp hết hạn hoặc đã hết hạn.": "Facebook accounts have an Access Token that is expiring soon or has already expired.",
  "Kiểm tra ngay": "Check now",
  "Cảnh báo lỗi Agent": "Agent error alert",
  "AI Agent gặp": "AI Agent encountered",
  "lỗi trong 24 giờ qua.": "errors in the past 24 hours.",
  "Xem nhật ký": "View logs",
  "📊 Báo cáo hôm nay": "📊 Today's report",
  "Agent đang chạy": "Agent is running",
  "Agent chưa hoạt động hôm nay": "Agent has not run yet today",
  "Gần nhất:": "Latest:",
  "Lợi nhuận hôm nay": "Profit today",
  "ROAS hôm nay": "ROAS today",
  "Agent điều chỉnh": "Agent adjustments",
  "Chi phí AI (ước tính)": "AI cost (estimated)",
  "đơn. Agent tăng ngân sách": "orders. Agent increased budget",
  "lần (": "times (",
  "vào ban ngày), giảm": "during the daytime), decreased",
  "lần; gọi AI ~": "times; called AI ~",
  "lần.": "times.",
  "lỗi hôm nay": "errors today",
  "Thay đổi ngân sách hôm nay": "Budget changes today",
  "▲ Tăng": "▲ Increase",
  "▼ Giảm": "▼ Decrease",
  "Chưa có thay đổi ngân sách nào hôm nay — agent đang theo dõi, chờ giờ vàng / thời điểm phù hợp.": "No budget changes yet today — the agent is monitoring, waiting for the golden hour / the right moment.",
  "Kết quả kinh doanh": "Business results",
  "Chi phí ads": "Ad spend",
  "Lợi nhuận (DT − ads)": "Profit (Revenue − ads)",
  "Doanh thu đã thanh toán, đã khớp campaign (theo tài khoản đã chọn)": "Paid revenue, matched to a campaign (for the selected accounts)",
  "Tổng doanh thu đã thanh toán (mọi route, cả đơn chưa gắn campaign)": "Total paid revenue (all routes, including orders not linked to a campaign)",
  "đơn": "orders",
  "chuyển đổi": "conversions",
  "Đã khớp campaign:": "Matched to campaign:",
  "Chiến dịch đang chạy": "Active campaigns",
  "Quy tắc đang bật": "Active rules",
  "Chi tiêu & doanh thu": "Spend & revenue",
  "Chi phí ads:": "Ad spend:",
  "Doanh thu:": "Revenue:",
  "Chưa có dữ liệu trong khoảng thời gian này.": "No data for this time range.",
  "Trạng thái AI Agent": "AI Agent status",
  "Chờ hoạt động": "Idle",
  "Hành động (24h)": "Actions (24h)",
  "Tổng ngân sách/ngày (camp đã tiêu)": "Total daily budget (campaigns with spend)",
  "Chi tiêu ads": "Ad spend",
  "Hoạt động gần nhất": "Last activity",
  "Nhật ký Agent gần đây": "Recent Agent logs",
  "Chưa có hoạt động nào của Agent.": "No Agent activity yet.",

  // ---- Settings ----
  "Vui lòng nhập mật khẩu hiện tại": "Please enter your current password",
  "Mật khẩu xác nhận không khớp": "Password confirmation does not match",
  "Tính năng đổi mật khẩu sẽ sớm được hỗ trợ.": "The change-password feature will be available soon.",
  "Cài đặt": "Settings",
  "Quản lý thông tin tài khoản và tùy chọn.": "Manage your account information and preferences.",
  "Thông tin tài khoản": "Account Information",
  "Mã người dùng": "User ID",
  "Đổi mật khẩu": "Change Password",
  "Mật khẩu hiện tại": "Current Password",
  "Mật khẩu mới": "New Password",
  "Xác nhận mật khẩu mới": "Confirm New Password",
  "Cập nhật mật khẩu": "Update Password",
  "Phiên đăng nhập": "Login Session",
  "Đăng xuất khỏi thiết bị này.": "Log out of this device.",
};
